import { useState, useEffect, useCallback } from 'react';
import { measurementsAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  Plus, Ruler, ChevronDown, ChevronRight, Star, AlertTriangle,
  ArrowRight, ArrowLeft, Check, X, TrendingUp, TrendingDown, Minus,
  Pencil, Clock, History, Save, Download
} from 'lucide-react';
import { generateMeasurementPDF, downloadPDF } from '@/lib/pdfGenerator';

const INCH_TO_CM = 2.54;
const CM_TO_INCH = 1 / 2.54;

// Height is stored and input in cm, other fields in inches
const HEIGHT_FIELDS = ['height'];

function cmToFeetInches(cm) {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}'${inches}"`;
}

const TYPE_LABELS = {
  body: 'Body Measurement', self_measured: 'Self-Measured', finished_garment: 'Finished Garment',
};
const TYPE_DESCRIPTIONS = {
  body: 'Taken in person by a tailor or salesperson',
  self_measured: 'Client takes their own measurements with guided instructions',
  finished_garment: 'Measurements from an existing well-fitting garment',
};
const GARMENT_LABELS = {
  suit: 'Suit', shirt: 'Shirt', trousers: 'Trousers', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama', general: 'General (Full Body)',
};
const GARMENT_GROUPS = [
  { label: 'Jackets & Coats', items: ['suit', 'sherwani', 'bandhgala'] },
  { label: 'Shirts & Kurtas', items: ['shirt', 'kurta'] },
  { label: 'Bottoms', items: ['trousers', 'pyjama'] },
  { label: 'Vests', items: ['nehru_vest'] },
  { label: 'Complete', items: ['general'] },
];

// Validation ranges in inches
const VALIDATION_RANGES = {
  neck: [13, 20], shoulder_width: [14, 22], shoulder_drop: [1.5, 4],
  cross_front: [12, 20], cross_back: [14, 22],
  chest: [30, 55], lower_chest: [28, 54], stomach: [28, 59],
  waist: [24, 51], hips: [30, 55],
  bicep: [10, 20], forearm: [8, 16], wrist: [5.5, 8.7],
  sleeve_length: [20, 30], jacket_length: [24, 35],
  shirt_length: [24, 37], back_length: [14, 22],
  waistcoat_length: [18, 30], kurta_length: [31, 51],
  vest_length: [18, 30],
  trouser_waist: [24, 51], u_rise: [9, 17],
  thigh: [18, 31], thigh_lower: [14, 26],
  knee: [12, 22], calf: [11, 22], ankle: [7, 13],
  inseam: [26, 39], trouser_length: [35, 51],
  trouser_bottom_opening: [5.5, 12],
  height: [150, 211], weight: [88, 330],
};

function formatDateTime(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return isoStr; }
}

export default function MeasurementsTab({ clientId }) {
  const [measurements, setMeasurements] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [editingSetId, setEditingSetId] = useState(null);
  const [clientName, setClientName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [msRes, clientRes] = await Promise.all([
        measurementsAPI.list(clientId),
        clientsAPI.get(clientId),
      ]);
      setMeasurements(msRes.data);
      const c = clientRes.data;
      setClientName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    } catch (err) {
      console.error('Failed to load measurements', err);
    }
    try {
      const compRes = await measurementsAPI.compare(clientId);
      setComparison(compRes.data);
    } catch (err) {
      console.error('Failed to load comparison', err);
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeSet = measurements.find(m => m.is_active);
  const historicalSets = measurements.filter(m => !m.is_active);

  const handleDownloadPDF = (measurementSet) => {
    const doc = generateMeasurementPDF({
      clientName,
      measuredDate: measurementSet.measured_date,
      measuredBy: measurementSet.measured_by,
      garmentContext: measurementSet.garment_context,
      values: measurementSet.values,
    });
    downloadPDF(doc, `Measurements-${clientName.replace(/\s+/g, '_')}.pdf`);
    toast.success('Measurement PDF downloaded');
  };

  const toggleActive = async (setId) => {
    try {
      await measurementsAPI.update(setId, { is_active: true });
      toast.success('Active measurement set updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteSet = async (setId) => {
    try {
      await measurementsAPI.delete(setId);
      toast.success('Measurement set deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (<div className="space-y-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-32 w-full" /></div>);
  }

  if (wizardOpen) {
    return <MeasurementWizard clientId={clientId} onClose={() => setWizardOpen(false)} onSaved={() => { setWizardOpen(false); fetchData(); }} />;
  }

  if (editingSetId) {
    const setToEdit = measurements.find(m => m.id === editingSetId);
    if (setToEdit) {
      return <MeasurementEditor
        measurementSet={setToEdit}
        onClose={() => setEditingSetId(null)}
        onSaved={() => { setEditingSetId(null); fetchData(); }}
      />;
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Measurements</h3>
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{measurements.length} set{measurements.length !== 1 ? 's' : ''} recorded</p>
        </div>
        <Button data-testid="new-measurement-button" onClick={() => setWizardOpen(true)}
          className="h-10 rounded-xl px-5 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
          <Plus className="w-4 h-4 mr-2" /> New Measurement
        </Button>
      </div>

      {/* Active Set */}
      {activeSet ? (
        <Card className="rounded-xl border-2 border-[color:var(--brand-gold)] bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <div className="absolute inset-x-5 top-0 h-0.5" style={{ backgroundColor: 'var(--brand-gold)' }} />
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} fill="var(--brand-gold)" />
                  <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Active Measurements</CardTitle>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-[rgba(197,165,90,0.12)] text-[#1B2A4A] border-[rgba(197,165,90,0.3)]">
                    {TYPE_LABELS[activeSet.measurement_type] || activeSet.measurement_type}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-medium px-2 py-0.5 rounded-md">
                    {GARMENT_LABELS[activeSet.garment_context] || activeSet.garment_context}
                  </Badge>
                  <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                    {activeSet.measured_date} {activeSet.measured_by ? `\u00b7 by ${activeSet.measured_by}` : ''}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="download-measurement-pdf" variant="outline" size="sm"
                  onClick={() => handleDownloadPDF(activeSet)}
                  className="h-9 rounded-lg text-xs gap-1.5">
                  <Download className="w-3.5 h-3.5" /> PDF
                </Button>
                <Button data-testid="edit-active-measurement-button" variant="outline" size="sm"
                  onClick={() => setEditingSetId(activeSet.id)}
                  className="h-9 rounded-lg text-xs gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <MeasurementGrid values={activeSet.values} />
            {activeSet.notes && (
              <p className="text-xs mt-4 italic" style={{ color: 'var(--ink-muted)' }}>Note: {activeSet.notes}</p>
            )}
            <EditLogBanner editLog={activeSet.edit_log} />
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-10 text-center">
            <Ruler className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--brand-navy)', opacity: 0.3 }} strokeWidth={1.5} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No active measurements. Record the first set to get started.</p>
          </CardContent>
        </Card>
      )}

      {/* Comparison */}
      {comparison && comparison.items && comparison.items.length > 0 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <Collapsible open={showComparison} onOpenChange={setShowComparison}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-[color:var(--surface-2)] rounded-t-xl" style={{ transition: 'background-color 150ms' }}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Measurement Comparison</CardTitle>
                  {showComparison ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60">
                        <th className="text-left py-2 pr-4 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Field</th>
                        <th className="text-right py-2 px-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Active</th>
                        <th className="text-right py-2 px-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Previous</th>
                        <th className="text-right py-2 pl-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.items.map(item => {
                        const diff = item.diff_inches;
                        return (
                          <tr key={item.field_name} className={`border-b border-border/30 ${diff && diff !== 0 ? 'bg-[rgba(197,165,90,0.03)]' : ''}`}>
                            <td className="py-2 pr-4 font-medium" style={{ color: 'var(--navy-ink)' }}>{item.label}</td>
                            <td className="py-2 px-3 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                              {item.active_inches != null ? `${item.active_inches}"` : '\u2014'}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                              {item.previous_inches != null ? `${item.previous_inches}"` : '\u2014'}
                            </td>
                            <td className="py-2 pl-3 text-right tabular-nums">
                              {diff != null && diff !== 0 ? (
                                <span className="flex items-center justify-end gap-1" style={{ color: diff > 0 ? 'var(--warning)' : '#1E7A5F' }}>
                                  {diff > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                  {diff > 0 ? '+' : ''}{diff}"
                                </span>
                              ) : (
                                <span style={{ color: 'var(--ink-muted)' }}><Minus className="w-3 h-3 inline" /></span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] mt-3" style={{ color: 'var(--ink-muted)' }}>All values in inches</p>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}

      {/* Historical Sets */}
      {historicalSets.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>History</h4>
          {historicalSets.map(ms => (
            <HistoricalSetCard key={ms.id} ms={ms} onSetActive={toggleActive} onDelete={handleDeleteSet} onEdit={() => setEditingSetId(ms.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Edit Log Banner - shows last edit info inline
   ============================================================ */
function EditLogBanner({ editLog }) {
  const [showAll, setShowAll] = useState(false);
  if (!editLog || editLog.length === 0) return null;

  const lastEdit = editLog[editLog.length - 1];

  return (
    <div className="mt-4 pt-3 border-t border-border/50">
      <Collapsible open={showAll} onOpenChange={setShowAll}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 w-full text-left group" data-testid="edit-log-toggle">
            <History className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
            <span className="text-[11px] font-medium" style={{ color: 'var(--ink-muted)' }}>
              Last edited {formatDateTime(lastEdit.edited_at)} by {lastEdit.edited_by}
            </span>
            {editLog.length > 1 && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md ml-1">
                {editLog.length} edits
              </Badge>
            )}
            {showAll ? <ChevronDown className="w-3 h-3 ml-auto" /> : <ChevronRight className="w-3 h-3 ml-auto" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2 pl-5">
            {[...editLog].reverse().map((entry, i) => (
              <div key={entry.id || i} className="text-[11px] p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
                  <span className="font-medium" style={{ color: 'var(--navy-ink)' }}>{formatDateTime(entry.edited_at)}</span>
                  <span style={{ color: 'var(--ink-muted)' }}>by {entry.edited_by}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  {entry.changes && entry.changes.map((c, j) => (
                    <span key={j} style={{ color: 'var(--ink-muted)' }}>
                      {c.label}: {c.old_inches != null ? `${c.old_inches}"` : 'new'} {'\u2192'} {c.new_inches != null ? `${c.new_inches}"` : 'removed'}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/* ============================================================
   Measurement Grid - displays values in a neat grid (inches primary)
   ============================================================ */
function MeasurementGrid({ values }) {
  if (!values || values.length === 0) return <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No values recorded</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-3">
      {values.map(v => {
        const isHeight = HEIGHT_FIELDS.includes(v.field_name);
        return (
          <div key={v.field_name} data-testid={`measurement-value-${v.field_name}`}>
            <p className="text-[11px] font-medium" style={{ color: 'var(--ink-muted)' }}>{v.label}</p>
            <div className="flex items-baseline gap-1.5">
              {isHeight ? (
                <>
                  <span className="text-base font-semibold tabular-nums" style={{ color: 'var(--navy-ink)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    {v.value_cm}
                  </span>
                  <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>cm</span>
                  <span className="text-[11px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>/ {cmToFeetInches(v.value_cm)}</span>
                </>
              ) : (
                <>
                  <span className="text-base font-semibold tabular-nums" style={{ color: 'var(--navy-ink)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    {v.value_inches}
                  </span>
                  <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>in</span>
                  <span className="text-[11px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>/ {v.value_cm} cm</span>
                </>
              )}
            </div>
            {v.warning && (
              <div className="flex items-start gap-1 mt-0.5">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
                <p className="text-[10px] leading-tight" style={{ color: 'var(--warning)' }}>{v.warning}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   Historical Set Card
   ============================================================ */
function HistoricalSetCard({ ms, onSetActive, onDelete, onEdit }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="rounded-xl border border-border/80 bg-white" data-testid="historical-measurement-set">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-[color:var(--surface-2)] rounded-xl py-3" style={{ transition: 'background-color 150ms' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                    {GARMENT_LABELS[ms.garment_context] || ms.garment_context} {'\u2014'} {TYPE_LABELS[ms.measurement_type] || ms.measurement_type}
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                    {ms.measured_date} {ms.measured_by ? `\u00b7 by ${ms.measured_by}` : ''} {'\u00b7'} {ms.values.length} fields
                    {ms.edit_log && ms.edit_log.length > 0 && (
                      <span className="ml-1.5 px-1.5 py-0 rounded bg-[rgba(27,42,74,0.06)] text-[10px]">
                        edited {ms.edit_log.length}x
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); onSetActive(ms.id); }}
                  data-testid={`set-active-${ms.id}`}>
                  Set Active
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" style={{ color: 'var(--danger)' }}
                  onClick={(e) => { e.stopPropagation(); onDelete(ms.id); }}>
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            <Separator className="mb-4 opacity-60" />
            <MeasurementGrid values={ms.values} />
            {ms.notes && <p className="text-xs mt-3 italic" style={{ color: 'var(--ink-muted)' }}>Note: {ms.notes}</p>}
            <EditLogBanner editLog={ms.edit_log} />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/* ============================================================
   Measurement Editor - inline edit existing set
   ============================================================ */
function MeasurementEditor({ measurementSet, onClose, onSaved }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState(measurementSet.notes || '');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState({});

  useEffect(() => {
    // Load fields for the garment context
    measurementsAPI.getFields(measurementSet.garment_context, measurementSet.measurement_type === 'self_measured')
      .then(r => {
        setFields(r.data.fields);
        // Populate existing values
        const init = {};
        r.data.fields.forEach(f => { init[f.field_name] = ''; });
        measurementSet.values.forEach(v => {
          // Height stored as cm → show in cm for height field; show inches for others
          if (HEIGHT_FIELDS.includes(v.field_name)) {
            init[v.field_name] = String(v.value_cm || '');
          } else {
            init[v.field_name] = String(v.value_inches || '');
          }
        });
        setValues(init);
      })
      .catch(() => toast.error('Failed to load fields'));
  }, [measurementSet]);

  const updateValue = (fieldName, val) => {
    setValues(prev => ({ ...prev, [fieldName]: val }));
    if (val && !isNaN(val)) {
      const numVal = parseFloat(val);
      const range = VALIDATION_RANGES[fieldName];
      if (range && (numVal < range[0] || numVal > range[1])) {
        const unit = HEIGHT_FIELDS.includes(fieldName) ? 'cm' : '"';
        setWarnings(prev => ({ ...prev, [fieldName]: `Unusual (expected ${range[0]}${unit}-${range[1]}${unit})` }));
      } else {
        setWarnings(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
      }
    } else {
      setWarnings(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
    }
  };

  const filledValues = Object.entries(values).filter(([_, v]) => v && !isNaN(v));

  const handleSave = async () => {
    if (filledValues.length === 0) {
      toast.error('Please enter at least one measurement');
      return;
    }
    setSaving(true);
    try {
      await measurementsAPI.editValues(measurementSet.id, {
        values: filledValues.map(([field_name, val]) => {
          // Height: input is in cm, send as cm (backend converts)
          if (HEIGHT_FIELDS.includes(field_name)) {
            return { field_name, value_inches: parseFloat(val) / INCH_TO_CM };
          }
          return { field_name, value_inches: parseFloat(val) };
        }),
        notes: notes || null,
      });
      toast.success('Measurements updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onClose} className="h-9 px-2">
            <X className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
          </Button>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Edit Measurements</h3>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              {GARMENT_LABELS[measurementSet.garment_context]} {'\u2014'} {TYPE_LABELS[measurementSet.measurement_type]}
            </p>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <CardContent className="pt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
            {fields.map(field => {
              const isHeight = HEIGHT_FIELDS.includes(field.field_name);
              return (
              <div key={field.field_name} className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                  {field.label}
                </Label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      data-testid={`edit-input-${field.field_name}`}
                      type="number"
                      step={isHeight ? '1' : '0.1'}
                      value={values[field.field_name] || ''}
                      onChange={e => updateValue(field.field_name, e.target.value)}
                      placeholder={isHeight ? 'cm' : 'inches'}
                      className={`h-10 rounded-lg text-sm pr-10 ${warnings[field.field_name] ? 'border-[color:var(--warning)]' : ''}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{isHeight ? 'cm' : 'in'}</span>
                  </div>
                  {values[field.field_name] && !isNaN(values[field.field_name]) && (
                    <span className="text-xs tabular-nums whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}>
                      {isHeight ? cmToFeetInches(parseFloat(values[field.field_name])) : `${(parseFloat(values[field.field_name]) * INCH_TO_CM).toFixed(1)} cm`}
                    </span>
                  )}
                </div>
                {warnings[field.field_name] && (
                  <div className="flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
                    <p className="text-[10px] leading-tight" style={{ color: 'var(--warning)' }}>{warnings[field.field_name]}</p>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="h-20 rounded-lg text-sm resize-none"
              placeholder="Additional notes..." />
          </div>

          {/* Show existing edit history */}
          {measurementSet.edit_log && measurementSet.edit_log.length > 0 && (
            <EditLogBanner editLog={measurementSet.edit_log} />
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={onClose} className="h-10 rounded-xl px-4 text-sm">
              Cancel
            </Button>
            <Button data-testid="measurement-save-edit-button" onClick={handleSave} disabled={saving || filledValues.length === 0}
              className="h-11 rounded-xl px-6 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================
   Measurement Wizard - 4-step flow (inches default)
   ============================================================ */
function MeasurementWizard({ clientId, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [measurementType, setMeasurementType] = useState('');
  const [garmentContext, setGarmentContext] = useState('');
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [measuredBy, setMeasuredBy] = useState('');
  const [measuredDate, setMeasuredDate] = useState(new Date().toISOString().split('T')[0]);
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState({});

  const isSelfMeasured = measurementType === 'self_measured';

  // Load fields when garment context selected
  useEffect(() => {
    if (!garmentContext) return;
    measurementsAPI.getFields(garmentContext, isSelfMeasured).then(r => {
      setFields(r.data.fields);
      const init = {};
      r.data.fields.forEach(f => { init[f.field_name] = ''; });
      setValues(init);
    }).catch(() => toast.error('Failed to load fields'));
  }, [garmentContext, isSelfMeasured]);

  const updateValue = (fieldName, val) => {
    setValues(prev => ({ ...prev, [fieldName]: val }));
    if (val && !isNaN(val)) {
      const numVal = parseFloat(val);
      const range = VALIDATION_RANGES[fieldName];
      if (range && (numVal < range[0] || numVal > range[1])) {
        const unit = HEIGHT_FIELDS.includes(fieldName) ? 'cm' : '"';
        setWarnings(prev => ({ ...prev, [fieldName]: `Unusual (expected ${range[0]}${unit}-${range[1]}${unit})` }));
      } else {
        setWarnings(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
      }
    } else {
      setWarnings(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
    }
  };

  const filledValues = Object.entries(values).filter(([_, v]) => v && !isNaN(v));

  const handleSave = async () => {
    if (filledValues.length === 0) {
      toast.error('Please enter at least one measurement');
      return;
    }
    setSaving(true);
    try {
      await measurementsAPI.create(clientId, {
        measurement_type: measurementType,
        garment_context: garmentContext,
        measured_by: measuredBy || null,
        measured_date: measuredDate,
        is_active: isActive,
        notes: notes || null,
        values: filledValues.map(([field_name, val]) => {
          if (HEIGHT_FIELDS.includes(field_name)) {
            return { field_name, value_inches: parseFloat(val) / INCH_TO_CM };
          }
          return { field_name, value_inches: parseFloat(val) };
        }),
      });
      toast.success('Measurements saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save measurements');
    } finally {
      setSaving(false);
    }
  };

  const STEPS = ['Type', 'Garment', 'Measure', 'Review'];

  return (
    <div className="space-y-6">
      {/* Wizard Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onClose} className="h-9 px-2">
            <X className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
          </Button>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>New Measurement</h3>
        </div>
        <div className="hidden sm:flex items-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium
                ${step > i + 1 ? 'bg-[rgba(30,122,95,0.1)] text-[#1E7A5F]' : step === i + 1 ? 'bg-[color:var(--brand-navy)] text-white' : 'bg-[rgba(27,42,74,0.06)] text-[#475467]'}`}>
                {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className="w-6 h-px mx-1" style={{ backgroundColor: 'var(--border-color)' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Choose Type */}
      {step === 1 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Measurement Type</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <button key={key} onClick={() => { setMeasurementType(key); setStep(2); }}
                data-testid={`measurement-type-${key}`}
                className={`w-full text-left p-4 rounded-xl border flex items-center justify-between ${measurementType === key ? 'border-[color:var(--brand-gold)] bg-[rgba(197,165,90,0.06)] ring-1 ring-[color:var(--brand-gold)]' : 'border-border/60 hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-2)]'}`}
                style={{ transition: 'border-color 150ms, background-color 150ms' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>{label}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{TYPE_DESCRIPTIONS[key]}</p>
                </div>
                {measurementType === key && <Check className="w-5 h-5 flex-shrink-0 ml-3" style={{ color: 'var(--brand-gold)' }} />}
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Choose Garment Context */}
      {step === 2 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Garment Context</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            {GARMENT_GROUPS.map(group => (
              <div key={group.label}>
                <p className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--ink-muted)' }}>{group.label}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {group.items.map(g => (
                    <button key={g} onClick={() => { setGarmentContext(g); setStep(3); }}
                      data-testid={`garment-context-${g}`}
                      className={`p-3 rounded-xl border text-center ${garmentContext === g ? 'border-[color:var(--brand-gold)] bg-[rgba(197,165,90,0.04)]' : 'border-border/60 hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-2)]'}`}
                      style={{ transition: 'border-color 150ms, background-color 150ms' }}>
                      <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{GARMENT_LABELS[g]}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-start pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="h-10 rounded-xl px-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Enter Measurements (in inches) */}
      {step === 3 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Enter Measurements</CardTitle>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                  {GARMENT_LABELS[garmentContext]} {'\u2014'} {TYPE_LABELS[measurementType]}
                  {isSelfMeasured && <span className="ml-2 px-1.5 py-0.5 rounded bg-[rgba(197,165,90,0.12)] text-[11px]">Instructions shown below each field</span>}
                </p>
                <p className="text-[10px] mt-1 font-medium" style={{ color: 'var(--brand-gold)' }}>All fields optional {'\u00b7'} Height in cm, other fields in inches</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Meta fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Measured By</Label>
                <Input value={measuredBy} onChange={e => setMeasuredBy(e.target.value)} placeholder="Name" className="h-10 rounded-lg text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Date</Label>
                <Input type="date" value={measuredDate} onChange={e => setMeasuredDate(e.target.value)} className="h-10 rounded-lg text-sm" />
              </div>
              <div className="flex items-end gap-3 pb-0.5">
                <div className="flex items-center gap-2">
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                  <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Set as Active</Label>
                </div>
              </div>
            </div>

            <Separator className="opacity-60" />

            {/* Measurement fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              {fields.map(field => {
                const isHeight = HEIGHT_FIELDS.includes(field.field_name);
                return (
                <div key={field.field_name} className="space-y-1.5">
                  <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                    {field.label}
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        data-testid={`measurement-input-${field.field_name}`}
                        type="number"
                        step={isHeight ? '1' : '0.1'}
                        value={values[field.field_name] || ''}
                        onChange={e => updateValue(field.field_name, e.target.value)}
                        placeholder={isHeight ? 'cm' : 'inches'}
                        className={`h-10 rounded-lg text-sm pr-10 ${warnings[field.field_name] ? 'border-[color:var(--warning)]' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{isHeight ? 'cm' : 'in'}</span>
                    </div>
                    {values[field.field_name] && !isNaN(values[field.field_name]) && (
                      <span className="text-xs tabular-nums whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}>
                        {isHeight ? cmToFeetInches(parseFloat(values[field.field_name])) : `${(parseFloat(values[field.field_name]) * INCH_TO_CM).toFixed(1)} cm`}
                      </span>
                    )}
                  </div>
                  {warnings[field.field_name] && (
                    <div className="flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
                      <p className="text-[10px] leading-tight" style={{ color: 'var(--warning)' }}>{warnings[field.field_name]}</p>
                    </div>
                  )}
                  {isSelfMeasured && field.instruction && (
                    <p className="text-[10px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{field.instruction}</p>
                  )}
                </div>
                );
              })}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                className="h-20 rounded-lg text-sm resize-none"
                placeholder="Additional notes..." />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} className="h-10 rounded-xl px-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(4)} disabled={filledValues.length === 0}
                className="h-10 rounded-xl px-5 text-sm" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                Review <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Save */}
      {step === 4 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Review & Save</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <p className="text-[10px] font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Type</p>
                <p className="text-sm font-medium capitalize" style={{ color: 'var(--navy-ink)' }}>{TYPE_LABELS[measurementType]}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <p className="text-[10px] font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Garment</p>
                <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{GARMENT_LABELS[garmentContext]}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <p className="text-[10px] font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Date</p>
                <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{measuredDate}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <p className="text-[10px] font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Active</p>
                <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{isActive ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {Object.keys(warnings).length > 0 && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(181,71,8,0.06)', border: '1px solid rgba(181,71,8,0.15)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} />
                  <p className="text-xs font-semibold" style={{ color: 'var(--warning)' }}>{Object.keys(warnings).length} measurement{Object.keys(warnings).length > 1 ? 's' : ''} flagged for review</p>
                </div>
                <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>These values are outside typical ranges but will still be saved.</p>
              </div>
            )}

            <Separator className="opacity-60" />

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-3">
              {filledValues.map(([fieldName, val]) => {
                const inches = parseFloat(val);
                const cm = (inches * INCH_TO_CM).toFixed(1);
                const label = fields.find(f => f.field_name === fieldName)?.label || fieldName;
                const warn = warnings[fieldName];
                return (
                  <div key={fieldName}>
                    <p className="text-[11px] font-medium" style={{ color: 'var(--ink-muted)' }}>{label}</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-base font-semibold tabular-nums" style={{ color: 'var(--navy-ink)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{inches}</span>
                      <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>in</span>
                      <span className="text-[11px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>/ {cm} cm</span>
                    </div>
                    {warn && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <AlertTriangle className="w-3 h-3" style={{ color: 'var(--warning)' }} />
                        <span className="text-[10px]" style={{ color: 'var(--warning)' }}>Flagged</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {notes && <p className="text-xs italic mt-2" style={{ color: 'var(--ink-muted)' }}>Note: {notes}</p>}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)} className="h-10 rounded-xl px-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Edit
              </Button>
              <Button data-testid="measurement-save-button" onClick={handleSave} disabled={saving}
                className="h-11 rounded-xl px-6 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                <Check className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Measurements'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
